import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import {
  GetActivityDetailResponseSchema,
  GetActivityLogsQuerySchema,
  GetActivityLogsResponseSchema,
  AutoAppliedSummaryQuerySchema,
  AutoAppliedSummaryResponseSchema,
  GetAffectedEmailsQuerySchema,
  GetAffectedEmailsResponseSchema,
} from '../schemas/activity'
import { defaultErrorResponses } from './errors'

const c = initContract()

/**
 * Activity Logs Contract - Audit trail for all user actions (apply, reject, delete, archive, label, etc.).
 * All endpoints require authMiddleware.
 * JSONB activity_details capture operation context: suggestion_id, track, labels, actions, confidence, phase1_match, llm_used, metadata.
 */
export const activityContract = c.router({
  /**
   * Retrieves paginated activity logs of all user actions (apply, reject, delete, archive, label, etc.).
   * Gets: limit (default: 20, max: 100), offset from query.
   * Returns: GetActivityLogsResponseSchema with entries: { id, user_id, operation_type, email_count, created_at, activity_details }.
   * Side Effects: None (read-only).
   */
  list: {
    method: 'GET',
    path: '/v1/manage/activity/logs',
    query: GetActivityLogsQuerySchema,
    responses: {
      200: GetActivityLogsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'List activity logs',
      description: 'Paginated audit trail of user actions (suggestions, classifications, label operations).',
    },
  },
  /**
   * Fetches complete details for a specific activity log entry including affected email count and action details.
   * Gets: activity id from path.
   * Returns: GetActivityDetailResponseSchema with full JSONB activity_details, email_ids, operation_id, affected_emails[].
   * Side Effects: None (read-only).
   */
  detail: {
    method: 'GET',
    path: '/v1/manage/activity/:id',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: GetActivityDetailResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Fetch activity detail',
      description: 'Get full details for an activity log entry including affected emails and action metadata.',
    },
  },
  /**
   * Returns email metadata for gmail_message_ids stored in an activity log entry.
   * Emails past 30-day retention return cached: false with null metadata.
   */
  affectedEmails: {
    method: 'GET',
    path: '/v1/manage/activity/:id/affected-emails',
    pathParams: z.object({ id: z.string() }),
    query: GetAffectedEmailsQuerySchema,
    responses: {
      200: GetAffectedEmailsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'List affected emails for an activity',
      description:
        'Returns email metadata (subject, sender, date) for gmail_message_ids in an activity log entry. Emails past 30-day retention return cached: false with null metadata.',
    },
  },
  /**
   * Returns a summary of auto-applied activity in a rolling time window (7d or 30d).
   * Pro-only - Free users receive 403.
   * Returns: count + most-recent 5 items triggered by auto_apply.
   * Side Effects: None (read-only).
   */
  getAutoAppliedSummary: {
    method: 'GET',
    path: '/v1/activity/auto-applied-summary',
    query: AutoAppliedSummaryQuerySchema,
    responses: {
      200: AutoAppliedSummaryResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Auto-applied summary (Pro only)',
      description: 'Rolling count and recent items for auto-applied emails. Returns 403 for Free users.',
    },
  },
})
